import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import * as i18next from 'i18next';
import moment from 'moment';
import router from 'src/router';
import { G2S } from 'core/g2s';
import * as api from 'core/api';
import * as flights from 'core/flights';
import * as payment from 'core/payment';
import * as utils from 'core/utils';
import PaymentFormComponent from 'src/components/payment/form';
import { DialogSbpQrComponent } from 'src/components/payment/sbp-qr';
import { ReservationService } from 'core/api';
// eslint-disable-next-line @typescript-eslint/no-var-requires
const WithRender = require('./template.html');

@WithRender
@Component({
    components: {
        'v-payment-form': PaymentFormComponent,
        'v-dialog-sbp-qr': DialogSbpQrComponent
    }
})
export default class OrderPaymentComponent extends Vue {
    $refs!: {
        dialogSbp: DialogSbpQrComponent;
    }
    
    @Prop()
    orderGuid: string;

    @Prop()
    billPublicId: string;

    @Prop()
    error: string;

    @Prop()
    message: string;

    bill: api.BillInfo | null = null;

    gaParams: utils.GoogleAnalyticsParams = new utils.GoogleAnalyticsParams();

    gaAlreadySubmitted: string[] = [];

    disabledServiceButtons: boolean = false;

    flight: flights.FlightModel;

    paymentModel: payment.PaymentModel | null = null;

    reservation: api.ReservationInfo | null = null;

    title: string = '';

    view: string = 'loading';
    viewError: string = '';

    redirectUrl: string | null = null;

    @Watch('$route')
    onRouteChanged() {
        this.loadModel();
    }

    mounted() {
        // Redirect to mobile
        if (G2S.config.mobileRedirectAllowed) {
            let url = G2S.config.mobileUrl + '/order/payment/' + this.orderGuid;

            if (this.billPublicId)
            {
                url += '/' + this.billPublicId;
            }

            if (this.$route.query && this.$route.query.message)
            {
                url += '/message-' + this.$route.query.message;
            }

            if (this.$route.query)
            {
                const currentUrl = new URL(window.location.href);

                if (this.$route.query.ErrorText && typeof this.$route.query.ErrorText === 'string') {
                    currentUrl.searchParams.set('ErrorText', this.$route.query.ErrorText.replace('\n', '_'));
                }

                url += currentUrl.search;
            }

            window.location.href = url;
            return;
        }

        this.loadModel();
    }

    get amountToPay(): number {
        let price = 0;

        if (this.reservation) {
            price = this.reservation.amount;

            if (this.bill && this.bill.amount) {
                price = this.bill.amount;
            }

            if (this.paymentModel && this.paymentModel.selectedPaymentType.commission) {
                price += this.paymentModel.selectedPaymentType.commission;
            }
        }

        return price;
    }

    get orderTitle(): string {
        return i18next.t('label_order_with_number', <any>{ i: this.reservation!.reservationNumber });
    }

    get orderUrl(): string {
        return '/order/' + this.reservation!.reservationPublicId;
    }

    getProductIcon(product: api.Product): string {
        let urlPrefix = '/i/icons/lg/';

        if (G2S.config.theme === 'aviasales') {
            urlPrefix = '/themes/aviasales/i/icons/lg/';
        }

        return urlPrefix + (product.details.iconLarge ? product.details.iconLarge : 'empty') + '.svg';
    }

    loadModel() {
        this.view = 'loading';
        this.title = i18next.t('title_payment');
        window.document.title = this.title;

        if (this.billPublicId) {
            new api.ReservationService().getReservationAndBillStatus(this.orderGuid, this.billPublicId).then(
                (result: api.GetReservationStatusResult) => {
                    this.onResultSuccess(result);
                },
                (error) => {
                    this.onResultError(error);
                }
            );
        } else {
            new api.ReservationService().getReservationStatus(this.orderGuid, true, false).then(
                (result: api.GetReservationStatusResult) => {
                    this.onResultSuccess(result);
                },
                (error) => {
                    this.onResultError(error);
                }
            );
        }
    }

    makePaymentRedirect(d: api.PaymentInfoRedirect) {
        if (d.method.toLowerCase() === 'get' && d.requestParameters.length === 0) {
            window.location.href = d.url;
        }
        else {
            const form: HTMLFormElement = window.document.createElement('form');

            form.method = d.method;
            form.action = d.url;

            for (let i: number = 0; i < d.requestParameters.length; i++) {
                const p = d.requestParameters[i];
                const input = window.document.createElement('input');
                input.value = p.paramValue;
                input.name = p.paramName;
                input.type = 'hidden';
                form.appendChild(input);
            }

            window.document.body.appendChild(form);

            // Wait for DOM issues.
            setTimeout(function () {
                form.submit();
            }, 1000);
        }
    }

    onResultError(error: any) {
        this.viewError = i18next.t('error_server_tryagain');

        if (error.response) {
            if (error.response.status === 404) {
                this.viewError = i18next.t('error_order_notfound');
            }
            console.log('XHR failed', error.response.status, error.response.data);
        } else {
            console.log('XHR error', error.message);
        }

        this.view = 'error';
    }

    onResultSuccess(result: api.GetReservationStatusResult) {
        this.redirectUrl = result.redirectUrl;

        if (result?.paymentTypes?.length > 0
            && result?.billInfo?.billStatus !== 'Payed') {
            this.reservation = result.reservationInfo;
            this.bill = result.billInfo;
            this.paymentModel = new payment.PaymentModel(result.paymentTypes);

            if (this.bill && !utils.String.startsWith(this.bill.billType, 'Reservation')) {
                this.title = i18next.t('title_bill_payment');
            } else {
                this.title = i18next.t('title_order_payment');
            }

            window.document.title = this.title;

            // Handle error messages on return after 3Ds.
            if (this.$route.query && typeof this.$route.query.ErrorText === 'string') {
                const paymentTypeByError = result.paymentTypes.find((item) =>
                    item.paymentTypeId === result.billInfo.paymentTypeId);
                (<any>this).$ga.sendEvent(
                    'flights.booking',
                    `error.payment.${paymentTypeByError ? '.' + paymentTypeByError.kind : ''}${this.bill ? '.' + this.bill.paymentTypeId : ''}`.toLowerCase(),
                    `flights.booking.error.payment${paymentTypeByError ? '.' + paymentTypeByError.kind : ''}${this.bill ? '.' + this.bill.paymentTypeId : ''}`.toLowerCase(),
                    this.gaParams
                );

                if (!this.reservation.parentReservationPublicId && '6200' === this.$route.query.ErrorCode && this.reservation.products.length > 0 && !this.hasFlightService(this.reservation.products)) {
                    new api.ReservationService().updateBookingSessionNotEnoughMoneyPopup({
                        reservationPublicId: this.reservation!.reservationPublicId
                    }).catch(
                        () => console.error('Error when sending the request')
                    );
                    (<any>this).$ga.sendEvent(
                        'payment',
                        'error',
                        'payment.error.limit.card',
                        this.gaParams
                    );
                    (<any>this.$refs).modalError6200.isActive = true;
                    // Return to keep loading state
                    return;
                }

                if (G2S.config.theme === 'aviasales') {
                    (<any>this.$root.$refs).fullScreenDialog.show({
                        title: i18next.t('error'),
                        text: this.$route.query.ErrorText,
                        firstBtnText: 'Попробовать ещё раз',
                        firstBtnFn: () => {
                            (<any>this.$root.$refs).fullScreenDialog.hide();
                        },
                        secondBtnText: 'Вернуться к заказу',
                        secondBtnFn: () => {
                            if (this.redirectUrl) {
                                location.href = this.redirectUrl;
                            } else {
                                (<any>this.$root.$refs).fullScreenDialog.hide();
                                const reservationPublicId = result.reservationInfo.parentReservationPublicId ?? result.reservationInfo.reservationPublicId;
                                router.push('/order/' + reservationPublicId);
                            }
                        }
                    });
                } else {
                    (<any>this.$root).$refs.dialogMessage.show({
                        text: `<div class="pre-line">${this.$route.query.ErrorText}</div>`,
                        persistent: true
                    });
                }
            }

            this.view = 'form';

            // Init GA params
            this.flight = new flights.FlightModel(result.flight, result);

            this.gaParams.dimension2 = this.flight.adapter + '_'; // Add underscore for regex filtering in GA
            this.gaParams.dimension3 = this.flight?.allFlightAirlinesCode?.length ? this.flight.allFlightAirlinesCode.join('_') : '';
            this.gaParams.metric3 = result.reservationInfo.passengers.length;
            this.gaParams.metric7 = Math.round(moment.duration(this.flight.legs[0].departureDate.clone().startOf('day').diff(moment().startOf('day'))).asDays());
        } else {
            let title = '';
            let message = i18next.t(result.billInfo && result.billInfo.billStatus === 'Payed'
                ? 'error_bill_already_payed'
                : 'error_payment_not_available');

            // Handle success message on return after 3Ds.
            if (this.$route.query && this.$route.query.message && this.$route.query.message === 'payment-success') {
                title = i18next.t('title_thank_you');
                message = i18next.t('label_payment_success_bill');

                if (result.billInfo
                    && utils.String.startsWith(result.billInfo.billType, 'Reservation')) {
                    message = i18next.t('label_payment_success_reservation');
                    (<any>this).$ga.sendPageview('/flights/booking/payment/thankyou', 'Flights / Booking / Payment / Thankyou');
                }
            }

            // Handle error messages on return after 3Ds.
            if (
                !result.reservationInfo.parentReservationPublicId
                && this.$route.query
                && this.$route.query.ErrorCode === '6200'
                && result.reservationInfo.products.length > 0
                && !this.hasFlightService(result.reservationInfo.products)
            ) {
                new api.ReservationService().updateBookingSessionNotEnoughMoneyPopup({
                    reservationPublicId: result.reservationInfo.reservationPublicId
                }).catch(
                    () => console.error('Error when sending the request')
                );
            }

            if (this.$route.query && typeof this.$route.query.ErrorText === 'string') {
                message = this.$route.query.ErrorText;
            }

            if (G2S.config.theme === 'aviasales') {
                if (this.$route.query.payment_status === 'success') {
                    (<any>this.$root.$refs).fullScreenDialog.show({
                        title: this.$route.query?.ErrorText ? i18next.t('error') : (title || undefined),
                        text: message,
                        firstBtnText: 'Перейти к заказу',
                        firstBtnFn: () => {
                            if (this.redirectUrl) {
                                location.href = this.redirectUrl;
                            } else {
                                (<any>this.$root.$refs).fullScreenDialog.hide();
                                const reservationPublicId = result.reservationInfo.parentReservationPublicId ?? result.reservationInfo.reservationPublicId;
                                router.push('/order/' + reservationPublicId);
                            }
                        }
                    });
                } else {
                    (<any>this.$root.$refs).fullScreenDialog.show({
                        title: this.$route.query?.ErrorText ? i18next.t('error') : (title || undefined),
                        text: message,
                        firstBtnText: 'Попробовать ещё раз',
                        firstBtnFn: () => {
                            (<any>this.$root.$refs).fullScreenDialog.hide();
                        },
                        secondBtnText: 'Вернуться к заказу',
                        secondBtnFn: () => {
                            if (this.redirectUrl) {
                                location.href = this.redirectUrl;
                            } else {
                                (<any>this.$root.$refs).fullScreenDialog.hide();
                                const reservationPublicId = result.reservationInfo.parentReservationPublicId ?? result.reservationInfo.reservationPublicId;
                                router.push('/order/' + reservationPublicId);
                            }
                        }
                    });
                }
            } else {
                (<any>this.$root).$refs.dialogMessage.show({
                    title: title,
                    text: `<div style="white-space: pre-line">${message}</div>`,
                    persistent: true,
                    btnOkHandler: () =>
                    {
                        const reservationPublicId = result.reservationInfo.parentReservationPublicId ?? result.reservationInfo.reservationPublicId;
                        router.push('/order/' + reservationPublicId);
                    }
                });
            }
        }
    }

    onSubmit() {
        const error = (<any>this.$refs).paymentForm.validate();

        if (error) {
            (<any>this.$root).$refs.dialogMessage.show({
                text: error
            });
        } else {
            this.view = 'processing';
            window.scrollTo(0, 0);

            const request: api.PayReservationRequest = {
                reservationPublicId: this.reservation!.reservationPublicId,
                paymentTypeId: this.paymentModel!.selectedPaymentType.paymentTypeId,
                confirmationPageUrlTemplate: G2S.config.desktopUrl + '/go/order/payment/_reservationPublicId_/_billPublicId_',
                bonusAmount: this.paymentModel!.selectedPaymentType.paymentTypeId !== 'PayLater' ? this.paymentModel!.bonusAmount : 0,
                clientDevice: G2S.config.clientDevice
            };

            if (this.bill && this.bill.publicId) {
                request.billPublicId = this.bill.publicId;
            }

            if (this.paymentModel!.selectedPaymentType.kind === 'CreditCardInplace') {
                request.creditCard = this.paymentModel!.getCardWithEncryptedPacket() || undefined;

                if (!request.creditCard) {
                    (<any>this.$root).$refs.dialogMessage.show({
                        title: i18next.t('error'),
                        text: i18next.t('error_payment_card_not_available')
                    });

                    this.view = 'form';

                    (<any>this).$ga.sendEvent(
                        'flights.booking',
                        `payment.${this.paymentModel!.selectedPaymentType.kind}.${this.paymentModel!.selectedPaymentType.paymentTypeId}.notavailable`.toLowerCase(),
                        `flights.booking.payment.${this.paymentModel!.selectedPaymentType.kind}.${this.paymentModel!.selectedPaymentType.paymentTypeId}.notavailable`.toLowerCase(),
                        this.gaParams
                    );

                    return null;
                }
            }

            const reservationService = new api.ReservationService();

            reservationService.pay(request).then(
                (result: api.CreateReservationResult) => {
                    if (result.paymentInfo) {
                        if (!this.reservation?.parentReservationPublicId && result.paymentInfo.errorCode === 6200 && this.reservation?.products && this.reservation.products.length > 0 && !this.hasFlightService(this.reservation.products)) {
                            new api.ReservationService().updateBookingSessionNotEnoughMoneyPopup({
                                reservationPublicId: result.reservationInfo.reservationPublicId
                            }).catch(
                                () => console.error('Error when sending the request')
                            );

                            (<any>this).$ga.sendEvent(
                                'payment',
                                'error',
                                'payment.error.limit.card',
                                this.gaParams
                            );

                            (<any>this.$refs).modalError6200.isActive = true;
                        } else if (result.paymentInfo.inPlace) {
                            
                            if (result.paymentInfo.inPlace.status === 'SbpQr' && result.paymentInfo.redirect) {
                                const showReturnLink = this.paymentModel!.paymentTypes.length > 1;
                                this.$refs.dialogSbp.show(result.paymentInfo.redirect.url, showReturnLink);
                                
                                const getPaymentQrStatusRequest: api.FinishPreauth3dsRequest = {
                                    reservationPublicId: result.reservationInfo.reservationPublicId,
                                    billPublicId: result.paymentInfo.billPublicId,
                                    paymentId: result.paymentInfo.paymentId
                                };

                                // polling the backend until payment status changes
                                reservationService.pollPaymentQrStatus(getPaymentQrStatusRequest)
                                    .then((pollResult) => {
                                        this.$refs.dialogSbp.close();

                                        if (result.paymentInfo?.redirect?.url) {
                                            location.href = result.paymentInfo.redirect.url;
                                        }

                                        this.handleFinishResponse(<api.CreateReservationResult>pollResult);
                                    })
                                    .catch((error) => {
                                        this.$refs.dialogSbp.close();
                                        this.view = 'form';
                                        this.$nextTick(() => {
                                            (<any>this.$refs).paymentForm.$el.scrollIntoView({ behavior: 'smooth' });
                                        });
                                        if (error) {
                                            (<any>this.$root).$refs.dialogMessage.show({
                                                text: error.message
                                            });
                                        }
                                    });

                            } else if (result.paymentInfo.inPlace.succeed) {
                                if (result.paymentInfo.inPlace.status === 'Preauth3ds'
                                    && result.paymentInfo.redirect) {
                                    console.log('Preauth3ds');

                                    utils.Http.hiddenFrameWithPost(result.paymentInfo.redirect);

                                    // Wait for DOM issues.
                                    setTimeout(() => {
                                        const finishRequest: api.FinishPreauth3dsRequest = {
                                            reservationPublicId: result.reservationInfo.reservationPublicId,
                                            billPublicId: result.paymentInfo.billPublicId,
                                            paymentId: result.paymentInfo.paymentId
                                        };

                                        reservationService.finishPreauth3ds(finishRequest).then(
                                            (result: api.CreateReservationResult) => {
                                                this.handleFinishResponse(result);
                                            },
                                            (error) => {
                                                if (error.response) {
                                                    console.log('api/finishPreauth3ds XHR failed', error.response.status, error.response.data);
                                                } else {
                                                    console.log('api/finishPreauth3ds XHR error', error.message);
                                                }

                                                this.view = 'form';

                                                if (G2S.config.theme === 'aviasales') {
                                                    (<any>this.$root.$refs).fullScreenDialog.show({
                                                        title: i18next.t('error_payment'),
                                                        text: i18next.t('error_payment_failed'),
                                                        firstBtnText: 'Попробовать ещё раз',
                                                        firstBtnFn: () => {
                                                            (<any>this.$root.$refs).fullScreenDialog.hide();
                                                        },
                                                        secondBtnText: 'Вернуться к заказу',
                                                        secondBtnFn: () => {
                                                            if (this.redirectUrl) {
                                                                location.href = this.redirectUrl;
                                                            } else {
                                                                (<any>this.$root.$refs).fullScreenDialog.hide();
                                                                const reservationPublicId = result.reservationInfo.parentReservationPublicId ?? result.reservationInfo.reservationPublicId;
                                                                router.push('/order/' + reservationPublicId);
                                                            }
                                                        }
                                                    });
                                                } else {
                                                    (<any>this.$root).$refs.dialogMessage.show({
                                                        title: i18next.t('error_payment'),
                                                        text: i18next.t('error_payment_failed')
                                                    });
                                                }
                                            }
                                        );
                                    }, 100);
                                } else if (result.paymentInfo.redirect) {
                                    // 3DS redirect
                                    this.makePaymentRedirect(result.paymentInfo.redirect);
                                } else {
                                    let m = i18next.t('label_payment_success_bill');

                                    if (this.bill
                                        && utils.String.startsWith(this.bill.billType, 'Reservation')) {
                                        m = i18next.t('label_payment_success_reservation');
                                        (<any>this).$ga.sendPageview('/flights/booking/payment/thankyou', 'Flights / Booking / Payment / Thankyou');
                                    }

                                    // Wait 5 seconds to give time to server to issue tickets and change status.
                                    setTimeout(() => {
                                        if (G2S.config.theme === 'aviasales') {
                                            (<any>this.$root.$refs).fullScreenDialog.show({
                                                title: i18next.t('title_thank_you'),
                                                text: i18next.t('label_payment_success_reservation'),
                                                firstBtnText: 'Перейти к заказу',
                                                firstBtnFn: () => {
                                                    if (this.redirectUrl) {
                                                        location.href = this.redirectUrl;
                                                    } else {
                                                        (<any>this.$root.$refs).fullScreenDialog.hide();
                                                        const reservationPublicId = result.reservationInfo.parentReservationPublicId ?? result.reservationInfo.reservationPublicId;
                                                        router.push('/order/' + reservationPublicId);
                                                    }
                                                }
                                            });
                                        } else {
                                            (<any>this.$root).$refs.dialogMessage.show({
                                                title: i18next.t('title_thank_you'),
                                                text: m,
                                                btnOkHandler: () => {
                                                    const reservationPublicId = result.reservationInfo.parentReservationPublicId ?? result.reservationInfo.reservationPublicId;
                                                    router.push('/order/' + reservationPublicId);
                                                }
                                            });
                                        }
                                    }, 5000);
                                }
                            }
                            else {
                                (<any>this).$ga.sendEvent(
                                    'flights.booking',
                                    `error.payment.${this.paymentModel!.selectedPaymentType.kind}.${this.paymentModel!.selectedPaymentType.paymentTypeId}`.toLowerCase(),
                                    `flights.booking.error.payment.${this.paymentModel!.selectedPaymentType.kind}.${this.paymentModel!.selectedPaymentType.paymentTypeId}`.toLowerCase(),
                                    this.gaParams
                                );
                                if (result.paymentInfo.inPlace.exceptionMessage) {
                                    console.error('Payment error: ' + result.paymentInfo.inPlace.exceptionMessage);
                                }

                                this.view = 'form';

                                if (G2S.config.theme === 'aviasales') {
                                    (<any>this.$root.$refs).fullScreenDialog.show({
                                        title: i18next.t('error_payment'),
                                        text: result.paymentInfo.inPlace.errorMessage,
                                        firstBtnText: 'Попробовать ещё раз',
                                        firstBtnFn: () => {
                                            (<any>this.$root.$refs).fullScreenDialog.hide();
                                        },
                                        secondBtnText: 'Вернуться к заказу',
                                        secondBtnFn: () => {
                                            if (this.redirectUrl) {
                                                location.href = this.redirectUrl;
                                            } else {
                                                (<any>this.$root.$refs).fullScreenDialog.hide();
                                                const reservationPublicId = this.reservation!.parentReservationPublicId ?? this.reservation!.reservationPublicId;
                                                router.push('/order/' + reservationPublicId);
                                            }
                                        }
                                    });
                                } else {
                                    (<any>this.$root).$refs.dialogMessage.show({
                                        title: i18next.t('error_payment'),
                                        text: result.paymentInfo.inPlace.errorMessage
                                    });
                                }
                            }
                        }
                        else if (result.paymentInfo.redirect) {
                            // Payment requires redirecting to external url
                            this.makePaymentRedirect(result.paymentInfo.redirect);
                        } else {
                            console.error('Payment: stange result');

                            const reservationPublicId = result.reservationInfo.parentReservationPublicId ?? result.reservationInfo.reservationPublicId;
                            router.push('/order/' + reservationPublicId);
                        }
                    } else {
                        const reservationPublicId = result.reservationInfo.parentReservationPublicId ?? result.reservationInfo.reservationPublicId;
                        router.push('/order/' + reservationPublicId);
                    }
                },
                (error) => {
                    if (error.response) {
                        console.log('api/Reservation/Pay XHR failed', error.response.status, error.response.data);

                        this.view = 'form';

                        if (error.response.status === 400) {
                            if (G2S.config.theme === 'aviasales') {
                                (<any>this.$root.$refs).fullScreenDialog.show({
                                    title: i18next.t('error'),
                                    text: (error.response.data && error.response.data.localizedMessage) ? error.response.data.localizedMessage : i18next.t('error_server_tryagain'),
                                    firstBtnText: 'Попробовать ещё раз',
                                    firstBtnFn: () => {
                                        (<any>this.$root.$refs).fullScreenDialog.hide();
                                    },
                                    secondBtnText: 'Вернуться к заказу',
                                    secondBtnFn: () => {
                                        if (this.redirectUrl) {
                                            location.href = this.redirectUrl;
                                        } else {
                                            (<any>this.$root.$refs).fullScreenDialog.hide();
                                            const reservationPublicId = this.reservation!.parentReservationPublicId ?? this.reservation!.reservationPublicId;
                                            router.push('/order/' + reservationPublicId);
                                        }
                                    }
                                });
                            } else {
                                (<any>this.$root).$refs.dialogMessage.show({
                                    title: i18next.t('error'),
                                    text: (error.response.data && error.response.data.localizedMessage) ? error.response.data.localizedMessage : i18next.t('error_server_tryagain')
                                });
                            }
                        } else {
                            (<any>this).$ga.sendPageview('/order/payment/error', 'Order / Payment / Error', this.gaParams);

                            if (G2S.config.theme === 'aviasales') {
                                (<any>this.$root.$refs).fullScreenDialog.show({
                                    title: i18next.t('error_payment'),
                                    text: (error.response.data && error.response.data.localizedMessage) ? error.response.data.localizedMessage : i18next.t('error_server_tryagain'),
                                    firstBtnText: 'Попробовать ещё раз',
                                    firstBtnFn: () => {
                                        (<any>this.$root.$refs).fullScreenDialog.hide();
                                    },
                                    secondBtnText: 'Вернуться к заказу',
                                    secondBtnFn: () => {
                                        if (this.redirectUrl) {
                                            location.href = this.redirectUrl;
                                        } else {
                                            (<any>this.$root.$refs).fullScreenDialog.hide();
                                            const reservationPublicId = this.reservation!.parentReservationPublicId ?? this.reservation!.reservationPublicId;
                                            router.push('/order/' + reservationPublicId);
                                        }
                                    }
                                });
                            } else {
                                (<any>this.$root).$refs.dialogMessage.show({
                                    title: i18next.t('error_payment'),
                                    text: (error.response.data && error.response.data.localizedMessage) ? error.response.data.localizedMessage : i18next.t('error_server_tryagain')
                                });
                            }
                        }
                    } else {
                        console.log('api/Reservation/Pay XHR error', error.message);
                    }
                }
            );
        }
    }
    onPaymentWithService() {
        const reservationService = new api.ReservationService();
        this.disabledServiceButtons = true;

        (<any>this).$ga.sendEvent(
            'payment',
            'error',
            'payment.error.limit.card.ok.product',
            this.gaParams
        );

        reservationService
            .getReservationAndBillStatus(this.reservation!.reservationPublicId, this.bill!.publicId, false)
            .then(
                (result: api.GetReservationStatusResult) => {
                    location.href = '/order/payment/' + result.reservationInfo!.reservationPublicId;
                },
                () => {
                    location.href = '/order/payment/' + this.reservation!.reservationPublicId;
                }
            );
    }

    onPaymentDeleteService() {
        const reservationService = new api.ReservationService();
        this.disabledServiceButtons = true;

        (<any>this).$ga.sendEvent(
            'payment',
            'error',
            'payment.error.limit.card.delete.product',
            this.gaParams
        );

        reservationService
            .getReservationAndBillStatus(this.reservation!.reservationPublicId, this.bill!.publicId, true)
            .then(
                (result: api.GetReservationStatusResult) => {
                    location.href = '/order/payment/' + result.reservationInfo!.reservationPublicId;
                },
                () => {
                    location.href = '/order/payment/' + this.reservation!.reservationPublicId;
                }
            );
    }
    
    handleFinishResponse(result: api.CreateReservationResult) {
        if (result.paymentInfo.inPlace.succeed && result.paymentInfo?.inPlace?.status !== 'Error') {
            if (result.paymentInfo.redirect) {
                // 3DS redirect
                this.makePaymentRedirect(result.paymentInfo.redirect);
            } else {
                let m = i18next.t('label_payment_success_bill');

                if (this.bill
                    && utils.String.startsWith(this.bill.billType, 'Reservation')) {
                    m = i18next.t('label_payment_success_reservation');
                    (<any>this).$ga.sendPageview('/flights/booking/payment/thankyou', 'Flights / Booking / Payment / Thankyou');
                }

                // Wait 5 seconds to give time to server to issue tickets and change status.
                setTimeout(() => {
                    if (G2S.config.theme === 'aviasales') {
                        (<any>this.$root.$refs).fullScreenDialog.show({
                            title: i18next.t('title_thank_you'),
                            text: m,
                            firstBtnText: 'Перейти к заказу',
                            firstBtnFn: () => {
                                if (this.redirectUrl) {
                                    location.href = this.redirectUrl;
                                } else {
                                    (<any>this.$root.$refs).fullScreenDialog.hide();
                                    const authToken = result.authToken ? '?auth_code=' + result.authToken : '';
                                    const reservationPublicId = result.reservationInfo.parentReservationPublicId ?? result.reservationInfo.reservationPublicId;
                                    router.push('/order/' + reservationPublicId + authToken);
                                }
                            }
                        });
                    } else {
                        (<any>this.$root).$refs.dialogMessage.show({
                            title: i18next.t('title_thank_you'),
                            text: m,
                            btnOkHandler: () => {
                                const authToken = result.authToken ? '?auth_code=' + result.authToken : '';
                                const reservationPublicId = result.reservationInfo.parentReservationPublicId ?? result.reservationInfo.reservationPublicId;
                                router.push('/order/' + reservationPublicId + authToken);
                            }
                        });
                    }
                }, 5000);
            }
        }
        else {
            (<any>this).$ga.sendEvent(
                'flights.booking',
                `error.payment.${this.paymentModel!.selectedPaymentType.kind}.${this.paymentModel!.selectedPaymentType.paymentTypeId}`.toLowerCase(),
                `flights.booking.error.payment.${this.paymentModel!.selectedPaymentType.kind}.${this.paymentModel!.selectedPaymentType.paymentTypeId}`.toLowerCase(),
                this.gaParams
            );
            if (result.paymentInfo.inPlace.exceptionMessage) {
                console.error('Payment error: ' + result.paymentInfo.inPlace.exceptionMessage);
            }

            this.view = 'form';
            
            this.$refs.dialogSbp.close();

            if (G2S.config.theme === 'aviasales') {
                (<any>this.$root.$refs).fullScreenDialog.show({
                    title: i18next.t('error_payment'),
                    text: result.paymentInfo.inPlace.errorMessage,
                    firstBtnText: 'Попробовать ещё раз',
                    firstBtnFn: () => {
                        (<any>this.$root.$refs).fullScreenDialog.hide();
                    },
                    secondBtnText: 'Вернуться к заказу',
                    secondBtnFn: () => {
                        if (this.redirectUrl) {
                            location.href = this.redirectUrl;
                        } else {
                            (<any>this.$root.$refs).fullScreenDialog.hide();
                            const reservationPublicId = result.reservationInfo.parentReservationPublicId ?? result.reservationInfo.reservationPublicId;
                            router.push('/order/' + reservationPublicId);
                        }
                    }
                });
            } else {
                (<any>this.$root).$refs.dialogMessage.show({
                    title: i18next.t('error_payment'),
                    text: result.paymentInfo.inPlace.errorMessage
                });
            }

        }
    }

    // stop polling API if the user has closed the SBP dialog
    handleDialogSbpQrClosing() {
        ReservationService.needToStopSbpPolling = true;
    }

    hasFlightService(products: api.Product[]) {
        let has = false;

        for (const product of products) {
            if (!['Service', 'Insurance', 'Package'].includes(product.type)) {
                has = true;
                break;
            }
        }

        return has;
    }
}
